'use client';

import { useEffect, useRef, useState } from 'react';

interface InlineAdProps {
  showAds?: boolean;
}

declare global {
  interface Window {
    adsbygoogle: Array<Record<string, unknown>>;
  }
}

export default function InlineAd({ showAds = true }: InlineAdProps) {
  const adRef = useRef<HTMLModElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [adInitialized, setAdInitialized] = useState(false);
  const [scriptReady, setScriptReady] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  // Use Intersection Observer to only load ads when they come into viewport
  useEffect(() => {
    if (!showAds || !containerRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.disconnect(); // Stop observing once visible
          }
        });
      },
      {
        rootMargin: '200px', // Load 200px before entering viewport
      }
    );

    observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, [showAds]);

  // Wait for AdSense script to be loaded by Zaraz (only when ad is visible)
  useEffect(() => {
    if (!showAds || !isVisible) return;

    const checkAdSenseReady = () => {
      if (window.adsbygoogle) {
        setScriptReady(true);
        return true;
      }
      return false;
    };

    // Check immediately
    if (checkAdSenseReady()) return;

    // Poll for script availability (Zaraz loads asynchronously)
    const interval = setInterval(() => {
      if (checkAdSenseReady()) {
        clearInterval(interval);
      }
    }, 100);

    // Cleanup after 10 seconds
    const timeout = setTimeout(() => {
      clearInterval(interval);
      if (!window.adsbygoogle) {
        console.warn('AdSense script not loaded after 10s - Zaraz may not be configured');
      }
    }, 10000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [showAds, isVisible]);

  // Initialize ad once script is ready
   
  useEffect(() => {
    if (showAds && scriptReady && adRef.current && !adInitialized) {
      // Check if AdSense has already initialized this ad slot
      const adStatus = adRef.current.getAttribute('data-adsbygoogle-status');

      // Only initialize if not already done
      if (!adStatus || adStatus === 'unfilled') {
        try {
          // Push ad to AdSense queue
          (window.adsbygoogle = window.adsbygoogle || []).push({});
          setAdInitialized(true);
        } catch (err) {
          console.error('AdSense error:', err);
        }
      }
    }
  }, [showAds, scriptReady, adInitialized]);
   

  // Don't render anything if ads should not be shown
  if (!showAds) {
    return null;
  }

  return (
    <div ref={containerRef} className="my-8">
      {isVisible && (
        <ins
          ref={adRef}
          className="adsbygoogle"
          style={{ display: 'block', textAlign: 'center' }}
          data-ad-layout="in-article"
          data-ad-format="fluid"
          data-ad-client="ca-pub-8172864430997885"
          data-ad-slot="9357613281"
        />
      )}
    </div>
  );
}
